import React from 'react';
import MultiSelectDropdown from './ui/MultiSelectDropdown';
import Room from '../models/Room';
import Subject from '../models/Subject';

interface RoomSelectorProps {
    availableRooms: Room[];
    selectedRooms: string[];
    onChange: (selectedRooms: string[]) => void;
    subject?: Subject | null;
    className?: string;
}

/**
 * RoomSelector component for selecting rooms with subject-based filtering
 * 
 * Features:
 * - Multi-select dropdown for room selection
 * - Subject-based room filtering when subject is provided
 * - Default selection (all rooms) when no rooms are selected
 * - Empty state handling
 */
const RoomSelector: React.FC<RoomSelectorProps> = ({
    availableRooms,
    selectedRooms,
    onChange,
    subject,
    className = ''
}) => {
    // Filter rooms based on subject constraints
    const getFilteredRooms = (): Room[] => {
        if (!subject || !subject.allowedRooms || subject.allowedRooms.length === 0) {
            // No subject constraints, return all available rooms
            return availableRooms;
        }

        // Filter rooms based on subject's allowed rooms
        return availableRooms.filter(room =>
            subject.allowedRooms!.includes(room.name)
        );
    };

    const filteredRooms = getFilteredRooms();

    // Convert rooms to room names for the dropdown
    const roomOptions = filteredRooms.map(room => room.name);

    // Validate selected rooms against filtered options
    const validSelectedRooms = selectedRooms.filter(roomName =>
        roomOptions.includes(roomName)
    );

    // If selected rooms were filtered out, update the selection
    React.useEffect(() => {
        if (validSelectedRooms.length !== selectedRooms.length) {
            onChange(validSelectedRooms);
        }
    }, [validSelectedRooms.length, selectedRooms.length, onChange]);

    // Handle empty state
    if (filteredRooms.length === 0) {
        return (
            <div className={`text-sm text-gray-500 italic ${className}`}>
                {subject ?
                    `No rooms available for subject "${subject.name}"` :
                    'No rooms available'
                }
            </div>
        );
    }

    const handleRoomChange = (newSelectedRooms: string[]) => {
        onChange(newSelectedRooms);
    };

    const getPlaceholderText = (): string => {
        if (selectedRooms.length === 0) {
            return 'All rooms (default)';
        }
        return 'Select rooms';
    };

    return (
        <div className={className}>
            <MultiSelectDropdown
                options={roomOptions}
                selectedOptions={validSelectedRooms}
                onChange={handleRoomChange}
                placeholder={getPlaceholderText()}
            />
            {subject && subject.allowedRooms && subject.allowedRooms.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                    Filtered by subject constraints ({filteredRooms.length} of {availableRooms.length} rooms available)
                </div>
            )}
        </div>
    );
};

export default RoomSelector;